import { Equipment } from '@cf-shared/data-model/equipment';
import { ExerciseStat } from '@cf-shared/data-model/user-state';

/**
 * The one owner of "which machine does this exercise use in this weight unit" (df-74d639d01502).
 *
 * An `ExerciseStat` records a machine per weight unit in `equipmentIdByUnit: { kg?, lb? }`. Older
 * documents, and every pick in the equipment picker to this day, also carry the single
 * `equipmentId` field from before machines were recorded per unit. Outside the data model, this is
 * the only app code that reads or writes either field, with two exceptions. Telemetry payloads and
 * the E2E summary readout log the raw stored shape as it is. And the older load migration in
 * `user-db.service.ts` still copies the obsolete `weightConfigId` into `equipmentId`.
 *
 * Every function here keeps the exact rule its call site applied before the move. Where two sites
 * answer a similar question differently, both rules live here side by side, with a comment saying
 * how they differ. They were not merged, because merging them would change what gets stored.
 */

export type EquipmentSlots = NonNullable<ExerciseStat['equipmentIdByUnit']>;

// ---- reading -------------------------------------------------------------------------------

/** the machine id filed under `unitKey`, with no validity check and no legacy fallback */
export function equipmentIdForUnit(stat: ExerciseStat | undefined, unitKey: string): string | undefined {
  return stat?.equipmentIdByUnit?.[unitKey];
}

/** the legacy single-machine id, from before machines were recorded per unit */
export function legacyEquipmentId(stat: ExerciseStat | undefined): string | undefined {
  return stat?.equipmentId;
}

/**
 * the machine id filed under `unitKey`, or else the legacy single-machine id. Nothing here checks
 * that the machine exists or is configured in that unit. Only telemetry reads it now; a reader
 * that decides weights uses `equipmentIdForUnitOrUsableLegacy`.
 */
export function equipmentIdForUnitOrLegacy(stat: ExerciseStat | undefined, unitKey: string): string | undefined {
  return equipmentIdForUnit(stat, unitKey) || legacyEquipmentId(stat);
}

/**
 * the legacy single-machine id, unless the machine it names is configured in another unit than
 * `unitKey` (df-0caf8c4d43d4). The equipment picker writes the legacy field with whichever machine
 * was picked LAST, in whichever unit, so after the person deletes their machine for one unit the
 * field can still name the other unit's machine. Handing that one back breaks the #2876 rule and
 * brings back the #5345 state. A machine that recorded no unit, or that is missing from
 * `allEquipment`, is not judged here: the id is returned as before and the caller decides.
 */
export function legacyEquipmentIdUsableForUnit(
  stat: ExerciseStat | undefined, unitKey: string, allEquipment: Record<string, Equipment> | undefined,
): string | undefined {
  const legacyId = legacyEquipmentId(stat);
  if (legacyId && declaredUnitDiffersFromKey(allEquipment?.[legacyId], unitKey)) {
    return undefined;
  }
  return legacyId;
}

/**
 * the machine id filed under `unitKey`, or else the legacy single-machine id when that machine is
 * not configured in another unit (see `legacyEquipmentIdUsableForUnit`)
 */
export function equipmentIdForUnitOrUsableLegacy(
  stat: ExerciseStat | undefined, unitKey: string, allEquipment: Record<string, Equipment> | undefined,
): string | undefined {
  return equipmentIdForUnit(stat, unitKey) || legacyEquipmentIdUsableForUnit(stat, unitKey, allEquipment);
}

/** every machine id the stat records, kg slot first, then lb, then the legacy field */
export function allRecordedEquipmentIds(stat: ExerciseStat | undefined): string[] {
  return [stat?.equipmentIdByUnit?.kg, stat?.equipmentIdByUnit?.lb, stat?.equipmentId].filter((id): id is string => !!id);
}

/** whether either unit slot, or the legacy field, holds `equipmentId` */
export function statRecordsEquipment(stat: ExerciseStat | undefined, equipmentId: string): boolean {
  return !!stat && (
    (!!stat.equipmentIdByUnit && (stat.equipmentIdByUnit.kg === equipmentId || stat.equipmentIdByUnit.lb === equipmentId))
    || stat.equipmentId === equipmentId
  );
}

/**
 * df-80e9963022e5: both unit slots hold the same machine, and that machine declares the OTHER
 * unit. Two copies that once shared one id left this behind. A machine the person switched to the
 * other unit on the edit page sits in one slot only, so it does not count.
 */
export function isCollisionDamage(stat: ExerciseStat | undefined, unitKey: string, equipmentId: string | undefined, equipment: Equipment | undefined): boolean {
  const otherUnitKey = unitKey === 'kg' ? 'lb' : unitKey === 'lb' ? 'kg' : undefined;
  return declaredUnitDiffersFromKey(equipment, unitKey)
    && !!otherUnitKey && equipmentIdForUnit(stat, otherUnitKey) === equipmentId;
}

// ---- the #2876 rule: a machine's own unit must match the slot it is filed under ------------
//
// Three readings of "the machine's own unit" are in use. For a machine whose unit is 'kg' or 'lb'
// they agree. They differ for a machine that never recorded a unit, or recorded something else:
// - `declaresTheOtherUnit` only fires when the machine names the OTHER of kg/lb. Preselect's slot
//   clearing uses it.
// - `declaredUnitDiffersFromKey` fires for any declared unit that is not the slot's. The
//   collision check uses it.
// - `equipmentUnitOrKg` counts anything but 'lb' as kilograms. The unit-switch decision uses it.

/** whether the machine declares the other one of kg and lb than `unitKey` */
export function declaresTheOtherUnit(equipment: Equipment | undefined, unitKey: 'kg' | 'lb'): boolean {
  return !!equipment && equipment.weight_unit === (unitKey === 'kg' ? 'lb' : 'kg');
}

/** whether the machine declares a unit, and that unit is not `unitKey` */
export function declaredUnitDiffersFromKey(equipment: Equipment | undefined, unitKey: string): boolean {
  return !!equipment?.weight_unit && equipment.weight_unit !== unitKey;
}

/** the machine's own unit, where a machine that never recorded one counts as kilograms */
export function equipmentUnitOrKg(equipment: Equipment): 'kg' | 'lb' {
  return equipment.weight_unit === 'lb' ? 'lb' : 'kg';
}

// ---- writing -------------------------------------------------------------------------------

/** the stat's unit slots, created empty first if the stat has none */
export function ensureEquipmentSlots(stat: ExerciseStat): EquipmentSlots {
  if (!stat.equipmentIdByUnit) {
    stat.equipmentIdByUnit = {};
  }
  return stat.equipmentIdByUnit;
}

/** files `equipmentId` under `unitKey`, creating the slots if needed */
export function recordEquipmentForUnit(stat: ExerciseStat, unitKey: string, equipmentId: string): void {
  ensureEquipmentSlots(stat)[unitKey] = equipmentId;
}

/** empties the `unitKey` slot. A stat with no slots at all is left without them */
export function clearEquipmentForUnit(stat: ExerciseStat, unitKey: string): void {
  if (stat.equipmentIdByUnit) {
    stat.equipmentIdByUnit[unitKey] = undefined;
  }
}

/** writes the legacy single-machine field. The equipment picker still does this on every pick */
export function setLegacyEquipmentId(stat: ExerciseStat, equipmentId: string): void {
  stat.equipmentId = equipmentId;
}

/** empties the legacy single-machine field, if it holds anything */
export function clearLegacyEquipmentId(stat: ExerciseStat): void {
  if (stat.equipmentId) {
    stat.equipmentId = undefined;
  }
}

/** empties every slot, and the legacy field, that holds `equipmentId` */
export function forgetEquipmentOnStat(stat: ExerciseStat, equipmentId: string): void {
  if (stat.equipmentId === equipmentId) {
    stat.equipmentId = undefined;
  }
  if (stat.equipmentIdByUnit) {
    if (stat.equipmentIdByUnit.kg === equipmentId) {
      stat.equipmentIdByUnit.kg = undefined;
    }
    if (stat.equipmentIdByUnit.lb === equipmentId) {
      stat.equipmentIdByUnit.lb = undefined;
    }
  }
}

/** empties every slot and the legacy field, leaving an empty slot map */
export function forgetAllEquipmentOnStat(stat: ExerciseStat): void {
  stat.equipmentIdByUnit = {};
  stat.equipmentId = undefined;
}

// ---- the legacy migration ------------------------------------------------------------------

/**
 * The two ways the app moves the legacy single machine into the per-unit slots. They disagree,
 * and the disagreement is kept on purpose, because changing either changes stored documents:
 */
export enum LegacyEquipmentMigration {
  // the reader's migration (getEquipmentForExercise): only when the stat has no slots at all,
  // copy the legacy machine under `unitKey` and KEEP the legacy field
  CopyWhenNoSlotsKeepingLegacy = 'copy_when_no_slots_keeping_legacy',
  // the workout screen's preselect: copy it under `unitKey` if that slot is empty, then DELETE
  // the legacy field, whether or not it was copied
  MoveIntoEmptySlotDroppingLegacy = 'move_into_empty_slot_dropping_legacy',
}

/**
 * which slot the workout screen's preselect moves the legacy machine into (df-0caf8c4d43d4).
 * Normally the unit the exercise is shown in. But when the legacy machine declares the OTHER unit,
 * filing it under the shown unit would break the #2876 rule, so it goes to its own unit's slot
 * instead, and only if that slot is empty. Example: kg empty, lb L, legacy L, shown in kg — the
 * lb slot already holds L, so nothing is copied and the legacy field is just dropped.
 */
export function legacyMigrationUnit(
  stat: ExerciseStat | undefined, displayUnit: 'kg' | 'lb', allEquipment: Record<string, Equipment> | undefined,
): 'kg' | 'lb' {
  const legacyId = legacyEquipmentId(stat);
  if (legacyId && declaresTheOtherUnit(allEquipment?.[legacyId], displayUnit)) {
    return displayUnit === 'kg' ? 'lb' : 'kg';
  }
  return displayUnit;
}

/** returns whether the legacy machine was copied into the `unitKey` slot */
export function migrateLegacyEquipmentId(stat: ExerciseStat | undefined, unitKey: string, migration: LegacyEquipmentMigration): boolean {
  if (!stat?.equipmentId) {
    return false;
  }
  switch (migration) {
    case LegacyEquipmentMigration.CopyWhenNoSlotsKeepingLegacy: {
      if (stat.equipmentIdByUnit) {
        return false;
      }
      stat.equipmentIdByUnit = {};
      stat.equipmentIdByUnit[unitKey] = stat.equipmentId;
      return true;
    }
    case LegacyEquipmentMigration.MoveIntoEmptySlotDroppingLegacy: {
      const slots = ensureEquipmentSlots(stat);
      let copied = false;
      if (!slots[unitKey]) {
        slots[unitKey] = stat.equipmentId;
        copied = true;
      }
      delete stat.equipmentId;
      return copied;
    }
  }
}
